import type { CaseListItem } from "@/services/caseServiceV2.types";
import { formatDate } from "@/utils/dateUtils";
import { tokenizeSearchText } from "@/utils/search/tokenSearch";
import MiniSearch from "minisearch";
import type { SearchResult } from "minisearch";

export interface CaseSearchDocument {
  id: string;
  text: string;
}

// Every term prefix-matches, unlike the shared tokenSearch.ts (which reserves
// prefix matching for the last word only): the Cases list has no word-drop
// fallback, so each word must stay lenient on its own as the user types it.
export const CASE_SEARCH_OPTIONS = { combineWith: "AND", prefix: true } as const;

export function buildCaseSearchIndex(cases: CaseListItem[]): MiniSearch<CaseSearchDocument> {
  const index = new MiniSearch<CaseSearchDocument>({
    fields: ["text"],
    tokenize: tokenizeSearchText,
  });

  const documents: CaseSearchDocument[] = cases.map((c) => ({
    id: c.case_id,
    text: [
      c.case_name,
      c.physician,
      c.firm_name,
      ...(c.analysts ?? []).map((a) => a.name),
      ...(c.qc_reviewers ?? []).map((r) => r.name),
      formatDate(c.exam_date),
      formatDate(c.due_date),
    ]
      .filter(Boolean)
      .join(" "),
  }));

  index.addAll(documents);
  return index;
}

export function searchCases(
  index: MiniSearch<CaseSearchDocument>,
  rawQuery: string
): SearchResult[] {
  const query = tokenizeSearchText(rawQuery).join(" ");
  if (!query) return [];
  return index.search(query, CASE_SEARCH_OPTIONS);
}
